/**
 * Every sentence and decision the preview dialog shows, decided away from JSX.
 *
 * The dialog itself places strings: what the header states about the file, whether a text body
 * is worth fetching at all, and what a body that has nothing to draw says instead are all
 * answered here, where a test can state them. `now` is an argument for the same reason it is one
 * throughout this screen's view-model — a relative label read off `Date.now()` is a label no
 * test can pin.
 */

import { type DocumentPreviewKind, getFileExtensionLabel } from "@/lib/file-types";
import { formatFileSize, formatSmartDate } from "@/lib/formatters";

/**
 * The ⋯ menu's word for opening a file in the app.
 *
 * The verb is the one "View transcript" already uses, and what follows it names what opens, so a
 * person reads the item as the thing they are about to be shown rather than as a mode.
 */
export function documentPreviewMenuLabel(previewKind: DocumentPreviewKind): string {
  return PREVIEW_MENU_LABELS[previewKind];
}

const PREVIEW_MENU_LABELS: Record<DocumentPreviewKind, string> = {
  image: "View image",
  pdf: "View document",
  markdown: "View document",
  text: "View text",
};

/**
 * What the header's meta line reads when the row carries none of the three facts.
 *
 * The line is the dialog's description and cannot be empty; the bare word keeps the header at
 * the height it has for every other file rather than letting it collapse.
 */
export const DOCUMENT_PREVIEW_META_ABSENT_LABEL = "File";

/**
 * The most text this dialog will pull into the page.
 *
 * A preview is a look at a file, not a text editor: past a couple of megabytes the fetch, the
 * decode and the single `<pre>` or Markdown tree all cost more than the look is worth, and the
 * file is one press away in the footer either way.
 */
export const TEXT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Whether a body needs the file's bytes read into the page before it can be drawn.
 *
 * Markdown and plain text have to be decoded and laid out; an image and a PDF are `src`
 * attributes the browser fetches and draws itself, so nothing is requested for them here.
 */
export function previewKindReadsText(previewKind: DocumentPreviewKind): boolean {
  return previewKind === "markdown" || previewKind === "text";
}

/**
 * Whether a body fills the height it is given rather than deriving one from its content.
 *
 * An image and a PDF frame stretch to their container, so the panel has to hand them a height;
 * the text bodies are as tall as their words and scroll.
 */
export function previewKindIsFramed(previewKind: DocumentPreviewKind): boolean {
  return previewKind === "image" || previewKind === "pdf";
}

/** One labelled fact in the header's meta line. */
export type DocumentPreviewMetaPair = {
  key: "type" | "size" | "uploaded";
  label: string;
  value: string;
};

/** What the header needs about the file, all of it already on the row. */
export type DocumentPreviewFacts = {
  /** The name the bytes arrived under — the extension the Type pair reads. */
  fileName: string;
  sizeBytes: number | null;
  uploadedAt: number | null;
};

/**
 * The header's labelled pairs, in reading order, carrying only the facts the row has.
 *
 * A pair is omitted rather than filled with a dash or a zero: a label over nothing is a
 * question the header cannot answer, which is the rule the transcript viewer's own meta line
 * follows.
 */
export function buildDocumentPreviewMeta(
  facts: DocumentPreviewFacts,
  now: number
): DocumentPreviewMetaPair[] {
  const pairs: DocumentPreviewMetaPair[] = [];

  const extensionLabel = getFileExtensionLabel(facts.fileName);
  if (extensionLabel !== null) {
    pairs.push({ key: "type", label: "Type", value: extensionLabel });
  }

  const sizeLabel = formatFileSize(facts.sizeBytes);
  if (sizeLabel !== null) {
    pairs.push({ key: "size", label: "Size", value: sizeLabel });
  }

  if (facts.uploadedAt !== null) {
    pairs.push({
      key: "uploaded",
      label: "Uploaded",
      value: formatSmartDate(facts.uploadedAt, now),
    });
  }

  return pairs;
}

/**
 * Whether the file is past the cap, from the byte count the row already carries.
 *
 * Asked *before* the fetch, which is the whole point: a 40 MB log refused here costs nothing,
 * whereas one refused after the download has already spent the download. A row with no byte
 * count cannot be refused in advance — the hook applies the same cap to the blob it lands.
 */
export function isTextPreviewOverCap(sizeBytes: number | null | undefined): boolean {
  return typeof sizeBytes === "number" && sizeBytes > TEXT_PREVIEW_MAX_BYTES;
}

/**
 * A text or Markdown body's state.
 *
 * `over_cap` and `failed` are separate outcomes because they are separate facts about the file:
 * one is a file this dialog will not read, the other is one it could not.
 */
export type TextPreviewState =
  | { kind: "loading" }
  | { kind: "ready"; text: string }
  | { kind: "over_cap" }
  | { kind: "failed" };

/** What a body with nothing to draw says, and what it points at. */
export type PreviewFallback = {
  /** Why there is nothing on screen. */
  message: string;
  /** The way out, which is the footer's Download beside it. */
  hint: string;
};

/**
 * The two sentences for the two states that draw no text, or `null` while there is text or a
 * fetch still running.
 *
 * Both are written from the reader's side and end at the button that solves them: neither state
 * is one a person can do anything about in the dialog, so the sentence that names it has to name
 * the way out too.
 */
export function resolveTextPreviewFallback(state: TextPreviewState): PreviewFallback | null {
  if (state.kind === "over_cap") {
    return {
      message: "Too large to preview here.",
      hint: "Download it to read the whole file.",
    };
  }
  if (state.kind === "failed") {
    return {
      message: "This file couldn't be loaded.",
      hint: "Download it to open it yourself.",
    };
  }
  return null;
}

/**
 * What the PDF body says when pdf.js refuses the file.
 *
 * The same shape and the same ending as the text bodies' refusals: a preview that cannot draw
 * the file still has to leave a person at the button that hands them the bytes. The sentence
 * names the PDF rather than "this file", because a PDF that will not open is usually damaged or
 * password-protected, and a reader who knows that reaches for the right next thing.
 */
export const PDF_PREVIEW_FAILURE: PreviewFallback = {
  message: "This PDF couldn't be opened.",
  hint: "Download it to open it yourself.",
};

/**
 * The sizes a page is drawn at, smallest first, as multiples of the page's own size.
 *
 * Absolute rather than relative to the body: pdf.js reports a page's size in CSS pixels, so `1`
 * is the paper at the size it was authored at and means the same thing on a phone as on a
 * laptop. The two fits are stops of their own alongside these — a ladder of nothing but fits
 * puts a whole page down the region one press away from a page across it, which on a tall page
 * is most of the range in a single step.
 */
export const PDF_ZOOM_STOPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3] as const;

/**
 * How the pages are sized: to the width of the column, to a whole page down the region, or to a
 * multiple of the page's own size.
 *
 * The two fits are kinds rather than numbers because neither is a fixed multiple of anything.
 * Each is answered from the region as it is at that moment, so a phone turned on its side is a
 * different size for the same choice, and a fit that had been stored as the number it happened
 * to work out to would stop being a fit the moment the window changed.
 */
export type PdfZoom =
  | { kind: "fit-width" }
  | { kind: "fit-height" }
  | { kind: "scale"; scale: number };

export const PDF_FIT_WIDTH: PdfZoom = { kind: "fit-width" };
export const PDF_FIT_HEIGHT: PdfZoom = { kind: "fit-height" };

/**
 * What the two fits work out to against the page being read, or `null` before any page has
 * reported its own size.
 *
 * A fit is the region measured against the paper — `regionWidth / originalWidth` and
 * `regionHeight / originalHeight` — so only the component that measured both can say what it is,
 * and it hands the pair in. Until it can, the steps have nothing but the numeric stops.
 */
export type PdfFitScales = { fitWidth: number; fitHeight: number } | null;

/** What the region leaves a page, and the size of the page being read. */
export type PdfPageMetrics = {
  /** The width left inside the region, its padding already out of it. */
  regionWidth: number;
  /** The height left inside the region, or `null` before it has been measured. */
  regionHeight: number | null;
  /** The current page's height over its width. */
  ratio: number;
  /** The current page's own width in CSS pixels, or `null` before the page reported it. */
  originalWidth: number | null;
};

/**
 * How wide a page is drawn, which is the one number every other size on the screen follows from.
 *
 * The fits are answered from the region — the column's width, or the width a whole page down the
 * region works out to through the page's own ratio. A numeric stop is answered from the paper
 * instead, so 100% is the page at the size pdf.js reports for it. Each falls back to the column
 * when the measurement it needs has not arrived, which is the only width there is to draw at.
 */
export function pdfPageWidth(zoom: PdfZoom, metrics: PdfPageMetrics): number {
  const columnWidth = Math.max(1, Math.round(metrics.regionWidth));
  if (zoom.kind === "fit-width") return columnWidth;
  if (zoom.kind === "fit-height") {
    if (metrics.regionHeight === null || !(metrics.ratio > 0)) return columnWidth;
    return Math.max(1, Math.round(metrics.regionHeight / metrics.ratio));
  }
  const paperWidth = metrics.originalWidth;
  if (paperWidth === null || !(paperWidth > 0)) {
    return Math.max(1, Math.round(columnWidth * zoom.scale));
  }
  return Math.max(1, Math.round(paperWidth * zoom.scale));
}

/**
 * The size the pages are at now, as a multiple of the page's own size.
 *
 * A fit measured before any page reported its size has no answer, and reads as the paper's own
 * size: that is where the numeric stops are counted from, so the steps still work.
 */
export function effectiveZoomScale(zoom: PdfZoom, fits: PdfFitScales): number {
  if (zoom.kind === "scale") return zoom.scale;
  if (fits === null) return 1;
  return zoom.kind === "fit-width" ? fits.fitWidth : fits.fitHeight;
}

/** One size a step can land on, and the zoom that means it. */
export type PdfZoomStop = { scale: number; zoom: PdfZoom };

/**
 * How close two sizes have to be to count as one stop.
 *
 * A fit that works out to within half a percent of a numeric stop is that stop as far as anyone
 * looking at the pages can tell, and a ladder holding both would spend a press on a step nothing
 * moves for.
 */
const ZOOM_STOP_TOLERANCE = 0.005;

function sameZoomStop(a: number, b: number): boolean {
  return Math.abs(a - b) <= Math.max(a, b) * ZOOM_STOP_TOLERANCE;
}

/**
 * Every size the step buttons can land on, smallest first.
 *
 * The two fits are stops among the numeric ones rather than a ladder of their own, which is what
 * makes a step out of a fit an ordinary step. Where a fit and a number collapse into one stop the
 * fit is the one kept: it is the same size, and it is the one that follows the window and can say
 * so in the label.
 */
export function resolveZoomStops(fits: PdfFitScales): PdfZoomStop[] {
  const fitStops: PdfZoomStop[] =
    fits === null
      ? []
      : [
          { scale: fits.fitWidth, zoom: PDF_FIT_WIDTH },
          { scale: fits.fitHeight, zoom: PDF_FIT_HEIGHT },
        ].filter((stop) => stop.scale > 0);
  const numericStops: PdfZoomStop[] = PDF_ZOOM_STOPS.map((scale) => ({
    scale,
    zoom: { kind: "scale", scale },
  }));

  const stops: PdfZoomStop[] = [];
  for (const candidate of [...fitStops, ...numericStops]) {
    if (stops.some((kept) => sameZoomStop(kept.scale, candidate.scale))) continue;
    stops.push(candidate);
  }
  return stops.sort((a, b) => a.scale - b.scale);
}

/**
 * The stop above the size the pages are at, or the zoom itself when there is none — which is how
 * the button offering it knows it has nothing left.
 */
export function nextZoom(current: PdfZoom, fits: PdfFitScales): PdfZoom {
  const scale = effectiveZoomScale(current, fits);
  const above = resolveZoomStops(fits).find(
    (stop) => stop.scale > scale && !sameZoomStop(stop.scale, scale)
  );
  return above?.zoom ?? current;
}

/** The stop below it, on the same terms. */
export function previousZoom(current: PdfZoom, fits: PdfFitScales): PdfZoom {
  const scale = effectiveZoomScale(current, fits);
  const below = resolveZoomStops(fits).filter(
    (stop) => stop.scale < scale && !sameZoomStop(stop.scale, scale)
  );
  return below[below.length - 1]?.zoom ?? current;
}

/** Whether two zooms are the same size — which is how a step button knows it has nothing left. */
export function zoomsEqual(a: PdfZoom, b: PdfZoom): boolean {
  if (a.kind === "scale" && b.kind === "scale") return a.scale === b.scale;
  return a.kind === b.kind;
}

/**
 * The zoom, as the control reads it.
 *
 * The two fits are named rather than numbered: each is a size the window decides, and a
 * percentage over it would claim a fixed relationship to the paper that it does not have. A
 * numeric stop is a percentage of the page's own size, so 100% is the paper as authored.
 */
export function formatZoomLabel(zoom: PdfZoom): string {
  if (zoom.kind === "fit-width") return "Fit width";
  if (zoom.kind === "fit-height") return "Fit height";
  return `${Math.round(zoom.scale * 100)}%`;
}

/**
 * Where in the document the reader is, in the words the control shows.
 *
 * Both numbers are always present: a counter that hid the total would leave "Page 3" saying
 * nothing about how much is left, which is the one thing a scrolling column does not show.
 */
export function formatPageCounter(current: number, total: number): string {
  return `Page ${current} of ${total}`;
}

/**
 * How many pages either side of the one being read are drawn.
 *
 * react-pdf draws a page's canvas as soon as the page mounts, so a mounted document is a document
 * rendered end to end — on open, and again on every zoom step. Two pages each way is what a
 * scroll can reach before the next render lands: the page above, in reach of a flick back, and
 * two below, which is as far as a fast scroll gets at the widest rung on the ladder.
 */
export const PDF_RENDER_WINDOW = 2;

/**
 * Whether a page is drawn, or held open by a placeholder of its size.
 *
 * The window is centred on the page being read, and is not clamped to the document: a reader at
 * page 1 gets pages 1 to 3, not five pages of which two do not exist.
 */
export function isPageRendered(page: number, currentPage: number, window: number): boolean {
  return Math.abs(page - currentPage) <= window;
}

/**
 * The height of a page as a multiple of its width, before the document says otherwise.
 *
 * A4 portrait, because the guess is only ever read for a page whose own size has not arrived yet
 * and most of what people preview here is a document rather than a slide. The moment a page
 * reports its size, that page's own ratio is used instead and this number never applies to it
 * again.
 */
export const PDF_PLACEHOLDER_ASPECT_RATIO = 297 / 210;

/** Where one page wrapper sits in the scrolled column, and how tall it is. */
export type PdfPageOffset = { top: number; height: number };

/**
 * The page under a point in the scrolled column, 1-based.
 *
 * The point is the middle of what a reader can see, and the answer is the wrapper that contains
 * it, or — when it falls in the gap between two pages — the last wrapper that starts at or above
 * it. There is always an answer: a point above the first wrapper is page 1 and one past the last
 * is the final page, because a column of pages is never on no page at all.
 */
export function pageAtMidpoint(offsets: PdfPageOffset[], midpoint: number): number {
  if (offsets.length === 0) return 1;
  let page = 1;
  for (const [index, offset] of offsets.entries()) {
    if (offset.top > midpoint) break;
    page = index + 1;
    if (midpoint < offset.top + offset.height) break;
  }
  return Math.min(Math.max(page, 1), offsets.length);
}

/**
 * Where a reader is, in terms that survive the pages being resized: a page, and how far down it
 * the middle of the region sits.
 *
 * The fraction is what makes it survive. Every wrapper moves and every wrapper changes height
 * when the zoom changes, so a scroll position is meaningless across the change while "two thirds
 * of the way down page 34" is the same place before and after.
 */
export type PdfReadingAnchor = { page: number; fraction: number };

/** The anchor a point in the scrolled column stands for, clamped to the page it lands on. */
export function readingAnchor(offsets: PdfPageOffset[], midpoint: number): PdfReadingAnchor {
  const page = pageAtMidpoint(offsets, midpoint);
  const offset = offsets[page - 1];
  if (offset === undefined || !(offset.height > 0)) return { page, fraction: 0 };
  const fraction = (midpoint - offset.top) / offset.height;
  return { page, fraction: Math.min(Math.max(fraction, 0), 1) };
}

/**
 * The scroll position that puts an anchor back under the middle of the region.
 *
 * Never negative: the top of the document is as far up as a region scrolls, and asking for less
 * than that is how the first page's anchor arrives after a step down the ladder. The bottom needs
 * no such clamp — the region refuses a scroll past its own content on its own.
 */
export function scrollTopForAnchor(
  offsets: PdfPageOffset[],
  anchor: PdfReadingAnchor,
  clientHeight: number
): number {
  const offset = offsets[Math.min(Math.max(anchor.page, 1), offsets.length) - 1];
  if (offset === undefined) return 0;
  return Math.max(0, Math.round(offset.top + anchor.fraction * offset.height - clientHeight / 2));
}
